import React, { useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthContext from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardLayout from './pages/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import ClientsPage from './pages/ClientsPage';
import GroupsPage from './pages/GroupsPage';
import CentersPage from './pages/CentersPage';
import LoansPage from './pages/LoansPage';
import SavingsPage from './pages/SavingsPage';
import ReportsPage from './pages/ReportsPage';
import SARBRatesPage from './pages/SARBRatesPage';
import AuditTrailPage from './pages/AuditTrailPage';
import SettingsPage from './pages/SettingsPage';

// Accounting Hub + Sub-pages
import AccountingHubPage from './pages/AccountingHubPage';
import FrequentPostingsPage from './pages/FrequentPostingsPage';
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import CreateJournalPage from './pages/CreateJournalPage';
import ClosingEntriesPage from './pages/ClosingEntriesPage';
import SearchJournalPage from './pages/SearchJournalPage';
import AccountingRulesPage from './pages/AccountingRulesPage';
import FinancialActivitiesPage from './pages/FinancialActivitiesPage';
import MobileApiGuidePage from './pages/MobileApiGuidePage';
import AccrualsPage from './pages/AccrualsPage';
import MigrateBalancesPage from './pages/MigrateBalancesPage';
import ProvisioningPage from './pages/ProvisioningPage';

// Admin > Organization Hub + Sub-pages
import OrganizationHubPage from './pages/OrganizationHubPage';
import OrganizationPage from './pages/OrganizationPage';
import EmployeesPage from './pages/EmployeesPage';
import CurrencyConfigPage from './pages/CurrencyConfigPage';
import PaymentTypesPage from './pages/PaymentTypesPage';
import HolidaysPage from './pages/HolidaysPage';
import WorkingDaysPage from './pages/WorkingDaysPage';
import FundsPage from './pages/FundsPage';
import TellersPage from './pages/TellersPage';
import LoanProvisioningPage from './pages/LoanProvisioningPage';
import BulkImportPage from './pages/BulkImportPage';
import PasswordPreferencesPage from './pages/PasswordPreferencesPage';

// Admin > Organization - New Pages
import StandingInstructionsPage from './pages/StandingInstructionsPage';
import InvestorsPage from './pages/InvestorsPage';
import FundMappingPage from './pages/FundMappingPage';

// Admin > Products Hub + Sub-pages
import ProductsHubPage from './pages/ProductsHubPage';
import LoanProductsPage from './pages/LoanProductsPage';
import SavingsProductsPage from './pages/SavingsProductsPage';
import ChargesPage from './pages/ChargesPage';
import ShareProductsPage from './pages/ShareProductsPage';
import ShareAccountsPage from './pages/ShareAccountsPage';
import ShareAccountsLookupPage from './pages/ShareAccountsLookupPage';
import ProductMixPage from './pages/ProductMixPage';
import FixedDepositProductsPage from './pages/FixedDepositProductsPage';
import RecurringDepositProductsPage from './pages/RecurringDepositProductsPage';
import LeaseProductsPage from './pages/LeaseProductsPage';

// Admin > System Hub + Sub-pages
import SystemHubPage from './pages/SystemHubPage';
import RolesPermissionsPage from './pages/RolesPermissionsPage';
import ManageCodesPage from './pages/ManageCodesPage';
import ManageJobsPage from './pages/ManageJobsPage';
import ReportingCalendarPage from './pages/ReportingCalendarPage';
import ManageHooksPage from './pages/ManageHooksPage';
import MakerCheckerPage from './pages/MakerCheckerPage';
import ConfigurationsPage from './pages/ConfigurationsPage';
import AccountNumberPrefsPage from './pages/AccountNumberPrefsPage';
import ApprovalQueuePage from './pages/ApprovalQueuePage';

// Admin > Users
import UsersPage from './pages/UsersPage';

// New Feature Pages
import SmsCampaignsPage from './pages/SmsCampaignsPage';
import DataTablesPage from './pages/DataTablesPage';
import ExternalServicesPage from './pages/ExternalServicesPage';
import RateManagementPage from './pages/RateManagementPage';
import BulkLoanReassignmentPage from './pages/BulkLoanReassignmentPage';
import AdHocQueryPage from './pages/AdHocQueryPage';
import EntityMappingPage from './pages/EntityMappingPage';
import ManageSurveysPage from './pages/ManageSurveysPage';
import TwoFactorAuthPage from './pages/TwoFactorAuthPage';
import SanctionsPage from './pages/SanctionsPage';
import ServiceAccountsPage from './pages/ServiceAccountsPage';
import ComplianceHubPage from './pages/ComplianceHubPage';
import ExternalEventsPage from './pages/ExternalEventsPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import CreditScoreConfigPage from './pages/CreditScoreConfigPage';
import CreditLifeInsurancePage from './pages/CreditLifeInsurancePage';
import MobilePaymentsPage from './pages/MobilePaymentsPage';
import FinancialReportsPage from './pages/FinancialReportsPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import TellerDashboard from './pages/TellerDashboard';
import UserSettingsPage from './pages/UserSettingsPage';
import ClientLookupPage from './pages/ClientLookupPage';
import SavingsLookupPage from './pages/SavingsLookupPage';
import LoansLookupPage from './pages/LoansLookupPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-12 h-12 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Role-Gated Route Component
const RoleGatedRoute = ({ children, roles }) => {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) {
    return <div className="p-12 text-center"><h2 className="text-xl font-bold text-gray-700">Access Denied</h2><p className="text-gray-500 mt-2">You do not have permission to view this page.</p></div>;
  }
  return children;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-12 h-12 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin" /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

// Role-Aware Dashboard Index
const DashboardIndex = () => {
  const { user } = useAuth();
  const viewAsRole = sessionStorage.getItem('viewAsRole');
  const role = viewAsRole || user?.role;
  if (role === 'teller' || role === 'loan_officer') return <Navigate to="/dashboard/clients" replace />;
  return <DashboardHome />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardIndex />} />

        {/* Clients Module — role-based view */}
        <Route path="clients" element={<RoleBasedClientView />} />
        <Route path="clients/lookup" element={<ClientLookupPage />} />
        <Route path="clients/groups" element={<RoleBasedGroupsView />} />
        <Route path="clients/centers" element={<RoleBasedCentersView />} />
        <Route path="clients/shares" element={<RoleBasedSharesView />} />

        {/* Operations */}
        <Route path="loans" element={<RoleBasedLoansView />} />
        <Route path="savings" element={<RoleBasedSavingsView />} />

        {/* Accounting Hub */}
        <Route path="accounting" element={<AccountingHubPage />} />
        <Route path="accounting/frequent-postings" element={<FrequentPostingsPage />} />
        <Route path="accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
        <Route path="accounting/create-journal" element={<CreateJournalPage />} />
        <Route path="accounting/closing-entries" element={<ClosingEntriesPage />} />
        <Route path="accounting/search-journal" element={<SearchJournalPage />} />
        <Route path="accounting/rules" element={<AccountingRulesPage />} />
        <Route path="accounting/financial-activities" element={<FinancialActivitiesPage />} />
        <Route path="accounting/accruals" element={<AccrualsPage />} />
        <Route path="accounting/migrate-balances" element={<MigrateBalancesPage />} />
        <Route path="accounting/provisioning" element={<ProvisioningPage />} />

        {/* Reports */}
        <Route path="reports" element={<ReportsPage />} />
        <Route path="rates" element={<SARBRatesPage />} />
        <Route path="audit" element={<AuditTrailPage />} />

        {/* Admin > Organization Hub */}
        <Route path="admin/organization" element={<OrganizationHubPage />} />
        <Route path="admin/organization/offices" element={<OrganizationPage />} />
        <Route path="admin/organization/employees" element={<EmployeesPage />} />
        <Route path="admin/organization/currencies" element={<CurrencyConfigPage />} />
        <Route path="admin/organization/payment-types" element={<PaymentTypesPage />} />
        <Route path="admin/organization/holidays" element={<HolidaysPage />} />
        <Route path="admin/organization/working-days" element={<WorkingDaysPage />} />
        <Route path="admin/organization/funds" element={<FundsPage />} />
        <Route path="admin/organization/tellers" element={<TellersPage />} />
        <Route path="admin/organization/loan-provisioning" element={<LoanProvisioningPage />} />
        <Route path="admin/organization/bulk-import" element={<BulkImportPage />} />
        <Route path="admin/organization/password-preferences" element={<PasswordPreferencesPage />} />
        <Route path="admin/organization/standing-instructions" element={<StandingInstructionsPage />} />
        <Route path="admin/organization/investors" element={<InvestorsPage />} />
        <Route path="admin/organization/fund-mapping" element={<FundMappingPage />} />
        <Route path="admin/organization/sms-campaigns" element={<SmsCampaignsPage />} />
        <Route path="admin/organization/adhoc-query" element={<AdHocQueryPage />} />
        <Route path="admin/organization/entity-checks" element={<DataTablesPage />} />
        <Route path="admin/organization/bulk-reassignment" element={<BulkLoanReassignmentPage />} />

        {/* Admin > Products Hub */}
        <Route path="admin/products" element={<ProductsHubPage />} />
        <Route path="admin/products/loan-products" element={<LoanProductsPage />} />
        <Route path="admin/products/savings-products" element={<SavingsProductsPage />} />
        <Route path="admin/products/charges" element={<ChargesPage />} />
        <Route path="admin/products/share-products" element={<ShareProductsPage />} />
        <Route path="admin/products/product-mix" element={<ProductMixPage />} />
        <Route path="admin/products/fixed-deposits" element={<FixedDepositProductsPage />} />
        <Route path="admin/products/recurring-deposits" element={<RecurringDepositProductsPage />} />
        <Route path="admin/products/lease-products" element={<LeaseProductsPage />} />

        {/* Admin > System Hub */}
        <Route path="admin/system" element={<SystemHubPage />} />
        <Route path="admin/system/roles" element={<RolesPermissionsPage />} />
        <Route path="admin/system/codes" element={<ManageCodesPage />} />
        <Route path="admin/system/jobs" element={<ManageJobsPage />} />
        <Route path="admin/system/reporting-calendar" element={<ReportingCalendarPage />} />
        <Route path="admin/system/hooks" element={<ManageHooksPage />} />
        <Route path="admin/system/mobile-api-guide" element={<MobileApiGuidePage />} />
        <Route path="admin/system/maker-checker" element={<MakerCheckerPage />} />
        <Route path="admin/system/configurations" element={<ConfigurationsPage />} />
        <Route path="admin/system/account-number-prefs" element={<AccountNumberPrefsPage />} />
        <Route path="admin/system/data-tables" element={<DataTablesPage />} />
        <Route path="admin/system/reports" element={<ReportsPage />} />
        <Route path="admin/system/entity-mapping" element={<EntityMappingPage />} />
        <Route path="admin/system/external-services" element={<ExternalServicesPage />} />
        <Route path="admin/system/rate-management" element={<RateManagementPage />} />
        <Route path="admin/system/surveys" element={<ManageSurveysPage />} />
        <Route path="admin/system/two-factor" element={<TwoFactorAuthPage />} />
        <Route path="admin/system/sanctions" element={<SanctionsPage />} />
        <Route path="admin/system/service-accounts" element={<ServiceAccountsPage />} />
        <Route path="compliance" element={<ComplianceHubPage />} />
        <Route path="admin/system/external-events" element={<ExternalEventsPage />} />
        <Route path="admin/approval-queue" element={<ApprovalQueuePage />} />

        {/* Admin > Users */}
        <Route path="admin/users" element={<UsersPage />} />

        {/* Executive Dashboard (Manager/Admin only) */}
        <Route path="executive" element={<RoleGatedRoute roles={['admin', 'manager']}><ExecutiveDashboardPage /></RoleGatedRoute>} />

        {/* Credit Score Configuration (Admin/Manager only) */}
        <Route path="admin/system/credit-score-config" element={<RoleGatedRoute roles={['admin', 'manager']}><CreditScoreConfigPage /></RoleGatedRoute>} />
        <Route path="admin/system/credit-life-insurance" element={<RoleGatedRoute roles={['admin', 'manager']}><CreditLifeInsurancePage /></RoleGatedRoute>} />

        {/* Mobile Payments */}
        <Route path="mobile-payments" element={<MobilePaymentsPage />} />

        {/* Financial Reports */}
        <Route path="financial-reports" element={<RoleGatedRoute roles={['admin', 'manager', 'accountant']}><FinancialReportsPage /></RoleGatedRoute>} />

        {/* System Settings */}
        <Route path="admin/system/settings" element={<RoleGatedRoute roles={['admin']}><SystemSettingsPage /></RoleGatedRoute>} />

        {/* User Settings (all roles) */}
        <Route path="user-settings" element={<UserSettingsPage />} />

        {/* Legacy routes */}
        <Route path="organization" element={<OrganizationPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Role-based client view: tellers/loan officers see lookup, accountants see read-only lookup, others see full list
function RoleBasedClientView() {
  const { user } = useContext(AuthContext);
  const viewAsRole = sessionStorage.getItem('viewAsRole');
  const role = viewAsRole || user?.role;
  if (role === 'teller' || role === 'loan_officer') {
    return <ClientLookupPage showNewClient={true} />;
  }
  if (role === 'accountant') {
    return <ClientLookupPage showNewClient={false} />;
  }
  return <ClientsPage />;
}

// Role-based savings view: tellers/loan officers see lookup, others see full list
function RoleBasedSavingsView() {
  const { user } = useContext(AuthContext);
  const viewAsRole = sessionStorage.getItem('viewAsRole');
  const role = viewAsRole || user?.role;
  if (role === 'teller' || role === 'loan_officer') {
    return <SavingsLookupPage />;
  }
  return <SavingsPage />;
}

// Role-based loans view: tellers/loan officers see lookup, others see full list
function RoleBasedLoansView() {
  const { user } = useContext(AuthContext);
  const viewAsRole = sessionStorage.getItem('viewAsRole');
  const role = viewAsRole || user?.role;
  if (role === 'teller' || role === 'loan_officer') {
    return <LoansLookupPage />;
  }
  return <LoansPage />;
}

// Role-based shares view: tellers see lookup, loan officers have no access, others see full list
function RoleBasedSharesView() {
  const { user } = useContext(AuthContext);
  const viewAsRole = sessionStorage.getItem('viewAsRole');
  const role = viewAsRole || user?.role;
  if (role === 'loan_officer') {
    return <div className="p-12 text-center"><h2 className="text-xl font-bold text-gray-700">Access Denied</h2><p className="text-gray-500 mt-2">Share accounts are managed by Tellers and Managers.</p></div>;
  }
  if (role === 'teller') {
    return <ShareAccountsLookupPage />;
  }
  return <ShareAccountsPage />;
}

// Role-based groups view: tellers/loan officers see search-first, others see full list
function RoleBasedGroupsView() {
  const { user } = useContext(AuthContext);
  const viewAsRole = sessionStorage.getItem('viewAsRole');
  const role = viewAsRole || user?.role;
  if (role === 'teller' || role === 'loan_officer') {
    return <GroupsPage lookupMode={true} />;
  }
  return <GroupsPage />;
}

// Role-based centers view: tellers/loan officers see search-first, others see full list
function RoleBasedCentersView() {
  const { user } = useContext(AuthContext);
  const viewAsRole = sessionStorage.getItem('viewAsRole');
  const role = viewAsRole || user?.role;
  if (role === 'teller' || role === 'loan_officer') {
    return <CentersPage lookupMode={true} />;
  }
  return <CentersPage />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
